import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";

/**
 * LetterTile — single hex-clipped tile in the A–Z doctrine map.
 *
 * Marketing variant (default): static styling driven by `tier` ("core" | "advanced")
 *   - Core (A–H): full size, bright cyan, "Active" label, bobs subtly
 *   - Advanced (I–Z): atmospheric, smaller, full title visible, sub-pixel jitter
 *
 * Dashboard variant: tile reflects per-user `state` prop:
 *   "completed"        → green-cyan tint + check overlay
 *   "active"           → pulsing cyan ring + scale-up
 *   "available"        → standard cyan
 *   "upgrade-required" → atmospheric/dim; "// upgrade →" hover caption
 */
export default function LetterTile({
    letter,
    title,
    summary,
    tier,
    index,
    variant = "marketing",
    state = null,
}) {
    const isDashboard = variant === "dashboard";
    const isCore = tier === "core";
    const [hovered, setHovered] = useState(false);
    const ref = useRef(null);
    const [floatPhase] = useState(() => Math.random() * Math.PI * 2);
    const [floatY, setFloatY] = useState(0);

    // Resolve dashboard state → visual mode
    const dashMode = isDashboard ? state || "available" : null;
    const isCompleted = dashMode === "completed";
    const isActive = dashMode === "active";
    const isUpgrade = dashMode === "upgrade-required";

    // Bob ONLY core marketing tiles + dashboard `active`
    const shouldBob = isDashboard ? isActive : isCore;
    useEffect(() => {
        if (!shouldBob) return;
        let frame;
        const start = performance.now();
        const tick = (now) => {
            const t = (now - start) / 1000;
            setFloatY(Math.sin(t * 0.9 + floatPhase) * 1.6);
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [shouldBob, floatPhase]);

    // Size classes
    const dim = isDashboard
        ? "w-[48px] h-[55px] sm:w-[54px] sm:h-[62px]"
        : isCore
            ? "w-[68px] h-[76px] sm:w-[78px] sm:h-[88px]"
            : "w-[56px] h-[63px] sm:w-[64px] sm:h-[72px]";

    // Border + bg + glow — dashboard overrides
    let borderClass = "";
    let bgStyle = {};
    let extraGlow = "";
    if (isDashboard) {
        if (isCompleted) {
            borderClass = "border-[1.5px] border-tca-cyan-hi";
            bgStyle = { background: "linear-gradient(180deg, rgba(22,201,178,0.20) 0%, rgba(0,219,255,0.06) 100%)" };
            extraGlow = "shadow-[0_0_18px_rgba(110,247,255,0.32)]";
        } else if (isActive) {
            borderClass = "border-[1.5px] border-tca-cyan hover:scale-[1.06]";
            bgStyle = { background: "linear-gradient(180deg, rgba(0,219,255,0.24) 0%, rgba(0,219,255,0.04) 100%)" };
            extraGlow = "shadow-glow-cyan-lg";
        } else if (isUpgrade) {
            borderClass = "border-[1px] hover:scale-[1.04]";
            bgStyle = { background: "radial-gradient(circle at 50% 50%, rgba(0,219,255,0.05) 0%, rgba(0,219,255,0.01) 70%)", borderColor: "rgba(0,219,255,0.15)" };
        } else {
            // available
            borderClass = "border-[1.5px] border-tca-cyan/55 hover:border-tca-cyan hover:scale-[1.06]";
            bgStyle = { background: "linear-gradient(180deg, rgba(0,219,255,0.10) 0%, rgba(0,219,255,0.02) 100%)" };
            extraGlow = "shadow-glow-cyan-xs hover:shadow-glow-cyan-lg";
        }
    } else {
        // Marketing variant — original styling
        if (isCore) {
            borderClass = "border-[1.5px] border-tca-cyan/55 hover:border-tca-cyan hover:scale-[1.06]";
            extraGlow = "shadow-glow-cyan-xs hover:shadow-glow-cyan-lg";
            bgStyle = { background: "linear-gradient(180deg, rgba(0,219,255,0.16) 0%, rgba(0,219,255,0.02) 100%)" };
        } else {
            borderClass = "border-[1px] hover:scale-[1.04]";
            bgStyle = {
                background: "radial-gradient(circle at 50% 50%, rgba(0,219,255,0.06) 0%, rgba(0,219,255,0.015) 70%)",
                borderColor: "rgba(0,219,255,0.15)",
            };
        }
    }

    // Letter glyph color
    const letterColor = (() => {
        if (isDashboard) {
            if (isCompleted) return "text-tca-cyan-hi";
            if (isActive) return "text-tca-cyan-hi";
            if (isUpgrade) return "text-tca-ink-muted/50";
            return "text-tca-cyan-hi";
        }
        return isCore ? "text-tca-cyan-hi" : "";
    })();

    const labelColor = (() => {
        if (isDashboard) {
            if (isCompleted) return "rgba(110,247,255,0.95)";
            if (isActive) return "rgba(0,219,255,0.95)";
            if (isUpgrade) return "rgba(0,219,255,0.32)";
            return "rgba(0,219,255,0.85)";
        }
        return isCore ? "rgba(0,219,255,0.85)" : "rgba(0,219,255,0.40)";
    })();

    const labelText = isDashboard
        ? isCompleted ? "Complete"
            : isActive ? "Active"
                : isUpgrade ? "Advanced"
                    : "Ready"
        : isCore ? "Active" : title;

    return (
        <motion.div
            ref={ref}
            data-testid={`az-tile-${letter}`}
            data-tier={tier}
            data-state={dashMode || "—"}
            initial={{ opacity: 0, y: 14, scale: 0.92 }}
            whileInView={{ opacity: isUpgrade ? 0.62 : 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.55, delay: 0.03 * index, ease: [0.22, 1, 0.36, 1] }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className={`
                relative flex flex-col items-center
                ${!isCore && !isDashboard ? "az-advanced-jitter" : ""}
                ${isDashboard && isUpgrade ? "az-advanced-jitter" : ""}
            `}
            style={{ transform: `translateY(${floatY}px)` }}
        >
            <div
                className={`
                    relative ${dim} ${borderClass} ${extraGlow}
                    flex items-center justify-center backdrop-blur-sm
                    transition-all duration-400 ease-out
                `}
                style={{
                    clipPath: "polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)",
                    ...bgStyle,
                }}
            >
                {/* Active phase ring (dashboard) */}
                {isActive && (
                    <motion.span
                        aria-hidden="true"
                        animate={{ opacity: [0.35, 0.85, 0.35], scale: [0.96, 1.06, 0.96] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            clipPath: "polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)",
                            boxShadow: "inset 0 0 18px rgba(0,219,255,0.55)",
                        }}
                    />
                )}

                <span
                    className={`font-display leading-none ${
                        isDashboard
                            ? "font-semibold text-2xl sm:text-[26px]"
                            : isCore
                                ? "font-medium text-3xl sm:text-[34px]"
                                : "font-light text-[22px] sm:text-2xl"
                    } ${letterColor}`}
                    style={
                        (isDashboard && (isCompleted || isActive)) || (isCore && !isDashboard)
                            ? { textShadow: "0 0 10px rgba(0,219,255,0.55)" }
                            : isDashboard && isUpgrade
                                ? { color: "rgba(255,255,255,0.30)" }
                                : !isCore
                                    ? { color: "rgba(255,255,255,0.55)" }
                                    : {}
                    }
                >
                    {letter}
                </span>

                {/* Completed checkmark overlay */}
                {isCompleted && (
                    <motion.span
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.2 + index * 0.02, type: "spring", stiffness: 220, damping: 14 }}
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-tca-cyan-hi text-tca-bg-deep flex items-center justify-center"
                        style={{ boxShadow: "0 0 10px rgba(110,247,255,0.85)" }}
                    >
                        <Check size={9} strokeWidth={3} />
                    </motion.span>
                )}
            </div>

            {/* Label */}
            <div
                className={`mt-2 text-center max-w-[80px] sm:max-w-[92px] font-mono-tight text-[8.5px] sm:text-[9px] uppercase leading-tight tracking-[0.16em]`}
                style={{ color: labelColor }}
                data-testid={`az-tile-title-${letter}`}
            >
                {labelText}
            </div>

            {/* Hover summary card */}
            <AnimatePresence>
                {hovered && (
                    <motion.div
                        initial={{ opacity: 0, y: 6, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 6, scale: 0.96 }}
                        transition={{ duration: 0.18 }}
                        className="absolute z-30 left-1/2 -translate-x-1/2 top-full mt-3 w-[220px] sm:w-[260px] tca-glass-strong px-4 py-3 pointer-events-none"
                        style={{
                            border: isCompleted || isActive
                                ? "1px solid rgba(0,219,255,0.40)"
                                : isUpgrade
                                    ? "1px solid rgba(0,219,255,0.18)"
                                    : "1px solid rgba(0,219,255,0.28)",
                            boxShadow: (isCompleted || isActive)
                                ? "0 12px 32px rgba(0, 219, 255, 0.22)"
                                : "0 8px 24px rgba(0, 219, 255, 0.10)",
                        }}
                    >
                        <div className="flex items-baseline gap-2 mb-1.5">
                            <span className={`font-display text-xl leading-none ${isUpgrade ? "text-tca-cyan/55" : "text-tca-cyan"}`}>
                                {letter}
                            </span>
                            <span className="font-mono-tight text-[10px] uppercase tracking-[0.22em] text-tca-cyan/85">
                                {title}
                            </span>
                        </div>
                        <p className="text-[12px] leading-snug text-tca-ink-muted">
                            {summary}
                        </p>
                        {isDashboard && isUpgrade && (
                            <div className="mt-2 font-mono-tight text-[9px] uppercase tracking-[0.26em] text-tca-cyan/85">
                                // upgrade →
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
